import React from 'react';
import {
  FileCheck,
  UserCheck,
  Gavel,
  Wallet,
  Trophy,
  Package,
  Ban,
  Copyright,
  Scale,
  Landmark,
  RefreshCw,
  Mail,
} from 'lucide-react';
import '../../../../styles/components/privacy-policy.scss';
import HeaderBanner from '../../../common/HeaderBanner';
import i18n from '../../../../i18n';

/*
 * i18n.t(key, 'Default text') — the English default shows until you add the key
 * to your translation files (e.g. the Arabic file), so the page works immediately.
 */
class TermsAndConditionsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: 'acceptance',
    };
    this.observer = null;
    this.clickLock = false;
    this.lockTimer = null;
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.setupScrollSpy();
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.lockTimer);
  }

  // Highlights the sidebar item for the section currently on screen
  setupScrollSpy = () => {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.clickLock) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) this.setState({ activeSection: visible[0].target.id });
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
    );
    document.querySelectorAll('.privacy-section').forEach((el) => this.observer.observe(el));
  };

  handleSectionClick = (sectionId) => {
    this.clickLock = true;
    this.setState({ activeSection: sectionId });
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    clearTimeout(this.lockTimer);
    this.lockTimer = setTimeout(() => {
      this.clickLock = false;
    }, 800);
  };

  render() {
    const { activeSection } = this.state;

    const menuItems = [
      {
        id: 'acceptance',
        label: i18n.t('tos_nav_acceptance', 'Acceptance of Terms'),
        icon: FileCheck,
      },
      {
        id: 'eligibility',
        label: i18n.t('tos_nav_eligibility', 'Eligibility & Registration'),
        icon: UserCheck,
      },
      { id: 'bidding', label: i18n.t('tos_nav_bidding', 'Bidding Rules'), icon: Gavel },
      { id: 'payments', label: i18n.t('tos_nav_payments', 'Deposits & Payments'), icon: Wallet },
      { id: 'winning', label: i18n.t('tos_nav_winning', 'Winning an Auction'), icon: Trophy },
      { id: 'items', label: i18n.t('tos_nav_items', 'Item Descriptions'), icon: Package },
      { id: 'prohibited', label: i18n.t('tos_nav_prohibited', 'Prohibited Conduct'), icon: Ban },
      {
        id: 'intellectual-property',
        label: i18n.t('tos_nav_ip', 'Intellectual Property'),
        icon: Copyright,
      },
      {
        id: 'liability',
        label: i18n.t('tos_nav_liability', 'Limitation of Liability'),
        icon: Scale,
      },
      { id: 'governing-law', label: i18n.t('tos_nav_law', 'Governing Law'), icon: Landmark },
      {
        id: 'changes-terms',
        label: i18n.t('tos_nav_changes', 'Changes to Terms'),
        icon: RefreshCw,
      },
      { id: 'contact-us', label: i18n.t('contact_us'), icon: Mail },
    ];

    return (
      <div className="privacy-page">
        <HeaderBanner
          title={i18n.t('terms_and_conditions', 'Terms & Conditions')}
          subtitle={i18n.t(
            'terms_and_conditions_subtitle',
            'The rules that apply when you use Mzadcom and take part in our auctions.'
          )}
        />

        <div className="privacy-container">
          <aside className="privacy-sidebar">
            <nav>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => this.handleSectionClick(item.id)}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="privacy-content">
            <div className="update-date">{i18n.t('last_updated')}: 27 September 2026</div>

            <div className="intro-text">
              {i18n.t(
                'tos_intro',
                'These Terms & Conditions form a binding agreement between you and Mzadcom. Please read them carefully before registering, bidding or buying on our platform. By using the platform, you agree to these terms.'
              )}
            </div>

            {/* 1. Acceptance */}
            <section id="acceptance" className="privacy-section">
              <h2>{i18n.t('tos_s1_title', '1. Acceptance of Terms')}</h2>
              <p>
                {i18n.t(
                  'tos_s1_p1',
                  'By accessing or using the Mzadcom website or mobile application, you confirm that you have read, understood and agree to be bound by these Terms & Conditions and our Privacy Policy.'
                )}
              </p>
              <p>
                {i18n.t(
                  'tos_s1_p2',
                  'If you do not agree with any part of these terms, you must not use the platform.'
                )}
              </p>
            </section>

            {/* 2. Eligibility */}
            <section id="eligibility" className="privacy-section">
              <h2>{i18n.t('tos_s2_title', '2. Eligibility & Registration')}</h2>
              <p>{i18n.t('tos_s2_p1', 'To bid or buy on Mzadcom you must:')}</p>
              <ul>
                <li>
                  {i18n.t(
                    'tos_s2_l1',
                    'Be at least 18 years old and legally able to enter into binding contracts.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s2_l2',
                    'Provide accurate, complete and up-to-date registration information, including a valid civil ID or commercial registration where required.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s2_l3',
                    'Keep your login credentials confidential and notify us immediately of any unauthorised use of your account.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s2_l4',
                    'Maintain only one account unless we have approved otherwise in writing.'
                  )}
                </li>
              </ul>
            </section>

            {/* 3. Bidding */}
            <section id="bidding" className="privacy-section">
              <h2>{i18n.t('tos_s3_title', '3. Bidding Rules')}</h2>
              <p>
                {i18n.t(
                  'tos_s3_p1',
                  'All auctions are conducted online in accordance with the specific conditions shown on each auction page. In general:'
                )}
              </p>
              <ul>
                <li>
                  {i18n.t(
                    'tos_s3_l1',
                    'Each bid must be equal to or higher than the minimum increment displayed for the lot.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s3_l2',
                    'The closing time shown on the lot page is final, except where an automatic time extension applies to bids placed in the final minutes.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s3_l3',
                    'Mzadcom may reject any bid, or cancel or suspend any auction, where there is a technical error, suspected fraud or a legal requirement to do so.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s3_l4',
                    'Bidding on behalf of a seller to inflate the price (shill bidding) is strictly prohibited.'
                  )}
                </li>
              </ul>
              <h3>{i18n.t('tos_s3_h_binding', 'Bids are binding')}</h3>
              <p>
                {i18n.t(
                  'tos_s3_p2',
                  'Every bid you place is a binding offer to purchase. Bids cannot be withdrawn once submitted, so please review the item details carefully before bidding.'
                )}
              </p>
            </section>

            {/* 4. Payments */}
            <section id="payments" className="privacy-section">
              <h2>{i18n.t('tos_s4_title', '4. Deposits & Payments')}</h2>
              <p>
                {i18n.t(
                  'tos_s4_p1',
                  'Some auctions require a refundable participation deposit before you can bid. Prices are shown in Omani Rials (OMR) unless stated otherwise.'
                )}
              </p>
              <ul>
                <li>
                  {i18n.t(
                    'tos_s4_l1',
                    'Deposits and payments are processed through a licensed, PCI-DSS compliant payment gateway. Mzadcom does not store your full card details.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s4_l2',
                    'The winning bidder must pay the full amount, including any applicable fees and VAT, within the period stated on the auction page.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s4_l3',
                    'Failure to pay on time may result in forfeiture of the deposit and suspension of your account.'
                  )}
                </li>
              </ul>
            </section>

            {/* 5. Winning */}
            <section id="winning" className="privacy-section">
              <h2>{i18n.t('tos_s5_title', '5. Winning an Auction')}</h2>
              <p>
                {i18n.t(
                  'tos_s5_p1',
                  'When an auction closes, the highest valid bid that meets any reserve price wins the lot. You will receive a confirmation by email and in your account.'
                )}
              </p>
              <p>
                {i18n.t(
                  'tos_s5_p2',
                  'Ownership of the item transfers to you once full payment has been received and any required transfer procedures have been completed.'
                )}
              </p>
            </section>

            {/* 6. Items */}
            <section id="items" className="privacy-section">
              <h2>{i18n.t('tos_s6_title', '6. Item Descriptions & Condition')}</h2>
              <p>
                {i18n.t(
                  'tos_s6_p1',
                  'We make every reasonable effort to provide clear and accurate descriptions, photographs and documents for each lot. Descriptions are based on information provided by the seller.'
                )}
              </p>
              <p>
                {i18n.t(
                  'tos_s6_p2',
                  'Unless expressly stated otherwise, items are sold on an "as is, where is" basis. You are encouraged to inspect items during the published viewing period before bidding.'
                )}
              </p>
            </section>

            {/* 7. Prohibited */}
            <section id="prohibited" className="privacy-section">
              <h2>{i18n.t('tos_s7_title', '7. Prohibited Conduct')}</h2>
              <p>{i18n.t('tos_s7_p1', 'When using the platform you must not:')}</p>
              <ul>
                <li>
                  {i18n.t(
                    'tos_s7_l1',
                    'Use the platform for any unlawful, fraudulent or misleading purpose.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'tos_s7_l2',
                    'Interfere with the operation or security of the platform, including using bots or automated bidding tools not provided by us.'
                  )}
                </li>
                <li>
                  {i18n.t('tos_s7_l3', 'Collude with other users to manipulate auction results.')}
                </li>
                <li>
                  {i18n.t(
                    'tos_s7_l4',
                    'Attempt to complete a transaction outside the platform to avoid fees.'
                  )}
                </li>
              </ul>
              <p>
                {i18n.t(
                  'tos_s7_p2',
                  'We may suspend or close any account that breaches these terms.'
                )}
              </p>
            </section>

            {/* 8. IP */}
            <section id="intellectual-property" className="privacy-section">
              <h2>{i18n.t('tos_s8_title', '8. Intellectual Property')}</h2>
              <p>
                {i18n.t(
                  'tos_s8_p1',
                  'All content on the platform, including logos, text, graphics, software and design, is owned by or licensed to Mzadcom and is protected by applicable intellectual property laws. You may not copy, reproduce or distribute it without our prior written permission.'
                )}
              </p>
            </section>

            {/* 9. Liability */}
            <section id="liability" className="privacy-section">
              <h2>{i18n.t('tos_s9_title', '9. Limitation of Liability')}</h2>
              <p>
                {i18n.t(
                  'tos_s9_p1',
                  'To the maximum extent permitted by law, Mzadcom is not liable for any indirect, incidental or consequential loss arising from your use of the platform, including loss of profits, data or business opportunities. Our total liability in connection with any transaction is limited to the fees we received for that transaction.'
                )}
              </p>
            </section>

            {/* 10. Law */}
            <section id="governing-law" className="privacy-section">
              <h2>{i18n.t('tos_s10_title', '10. Governing Law')}</h2>
              <p>
                {i18n.t(
                  'tos_s10_p1',
                  'These terms are governed by the laws of the Sultanate of Oman. Any dispute arising from them shall be subject to the exclusive jurisdiction of the competent courts of the Sultanate of Oman.'
                )}
              </p>
            </section>

            {/* 11. Changes */}
            <section id="changes-terms" className="privacy-section">
              <h2>{i18n.t('tos_s11_title', '11. Changes to These Terms')}</h2>
              <p>
                {i18n.t(
                  'tos_s11_p1',
                  'We may update these terms from time to time. The updated version will be posted on this page with a new "Last updated" date. Continued use of the platform after changes are published means you accept the revised terms.'
                )}
              </p>
            </section>

            {/* 12. Contact */}
            <section id="contact-us" className="privacy-section">
              <h2>{i18n.t('tos_s12_title', '12. Contact Us')}</h2>
              <p>
                {i18n.t(
                  'tos_s12_p1',
                  'If you have any questions about these Terms & Conditions, please contact us:'
                )}
              </p>
              <div className="contact-box">
                <p>
                  <strong>{i18n.t('group_auction')}</strong>
                </p>
                <p>{i18n.t('office_address_info')}</p>
                <p>
                  Email: <a href="mailto:info@mzadcom.om">info@mzadcom.om</a>
                </p>
              </div>
            </section>
          </main>
        </div>
      </div>
    );
  }
}

export default TermsAndConditionsPage;
